import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { env } from "../config/env";
import { I18nContext } from "./context";
import {
  browserLocaleGuess,
  readStoredLocale,
  type Locale,
  writeStoredLocale,
} from "./locale";
import { type UiKey, uiFormat, uiString } from "./ui";

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    return readStoredLocale() ?? browserLocaleGuess();
  });

  const setLocale = useCallback((l: Locale) => {
    writeStoredLocale(l);
    setLocaleState(l);
  }, []);

  const t = useCallback((key: UiKey) => uiString(locale, key), [locale]);

  const tf = useCallback(
    (key: UiKey, vars: Record<string, string | number>) => uiFormat(locale, key, vars),
    [locale],
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, tf }),
    [locale, setLocale, t, tf],
  );

  useEffect(() => {
    document.documentElement.lang =
      locale === "uk" ? "uk" : locale === "es" ? "es" : "en";
    const desc = uiString(locale, "meta.description");
    const keywords = uiString(locale, "meta.keywords");
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", desc);
    const kwMeta = document.querySelector('meta[name="keywords"]');
    if (kwMeta) kwMeta.setAttribute("content", keywords);
    const ogTitle = `${env.appName} — ${uiString(locale, "meta.ogTitleSuffix")}`;
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute("content", ogTitle);
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute("content", desc);
    document
      .querySelector('meta[name="twitter:title"]')
      ?.setAttribute("content", ogTitle);
    document
      .querySelector('meta[name="twitter:description"]')
      ?.setAttribute("content", desc);

    const ld = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: env.appName,
      description: desc,
      applicationCategory: "DeveloperApplication",
      operatingSystem: "Web browser",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
      },
      keywords: keywords,
    };
    let ldEl = document.getElementById("dmeterline-jsonld") as HTMLScriptElement | null;
    if (!ldEl) {
      ldEl = document.createElement("script");
      ldEl.id = "dmeterline-jsonld";
      ldEl.type = "application/ld+json";
      document.head.appendChild(ldEl);
    }
    ldEl.textContent = JSON.stringify(ld);
  }, [locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
